import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ContactSuggestion } from '@/api/contacts'
import type { CallTask } from '@/api/tasks'
import type { Contact } from '@/types/contacts'
import type { TimelineEvent, TimelineResponse } from '@/types/timeline'

/** Filtres de la Boîte de réception (une conversation par ligne et par personne).
 * mine, mentions, unassigned, done : plan Team (collaboration d'équipe). */
export type InboxFilter = 'all' | 'open' | 'unread' | 'unanswered' | 'missed' | 'mine' | 'mentions' | 'unassigned' | 'done'

/** Un membre de l'équipe, tel que le serveur le résume. */
export interface MembreResume {
  user_id: number
  name: string
  initials: string
}

/** Nature du dernier échange avec la personne. */
export type InboxLastKind =
  | 'sms_in'
  | 'sms_out'
  | 'call_in'
  | 'call_out'
  | 'missed'
  | 'voicemail'

/** Une ligne de l'entreprise (numéro Callizy). */
export interface InboxLine {
  number: string
  /** Libellé donné dans Administration ; vide : on affiche le numéro. */
  label: string
}

export interface InboxLineSummary extends InboxLine {
  unread_threads: number
}

/** A piece of a search snippet; `hit` pieces are the searched words. */
export interface MorceauExtrait {
  text: string
  hit: boolean
}

/** Conversation found by the content of one of its exchanges (search). */
export interface InboxMatch {
  kind: 'sms' | 'call' | 'voicemail' | 'note'
  /** Timeline id of the exchange to show (`sms_12`, `call_8`…). */
  event_id: string | null
  at: string
  /** Exchanges of this conversation that contain the words. */
  count: number
  text: string
  parts: MorceauExtrait[]
}

export interface InboxItem {
  phone: string
  /** La ligne de la conversation ; null pour un ancien appel sortant sans ligne connue. */
  line: InboxLine | null
  /** Conversations de la même personne sur d'autres lignes. */
  other_lines: number
  /** Vide si le numéro n'a pas de fiche : on affiche alors le numéro. */
  name: string
  contact_id: number | null
  avatar_url: string | null
  last_at: string
  last: {
    kind: InboxLastKind
    text?: string
    duration?: number
    agent?: string
    ai?: boolean
  }
  unread: number
  unanswered: boolean
  /** Plan Team seulement : le responsable de la conversation. */
  assigned_to?: MembreResume | null
  /** Plan Team seulement : la conversation est terminée. */
  done?: boolean
  /** Plan Team seulement : la personne connectée y est citée (« @ ») et n'a pas encore ouvert le fil. */
  mentioned?: boolean
  /** Search only: found by the content of an exchange, not by name or number. */
  match?: InboxMatch
}

/** Mentions non lues de la personne connectée (toast à l'ouverture). */
export interface ResumeMentions {
  threads: number
  authors: string[]
}

export async function chargerResumeMentions(): Promise<ResumeMentions> {
  const { data } = await api.get<ResumeMentions>('/api/inbox/mentions/')
  return data
}

/** Filtres de la rangée (épinglés, dans l'ordre de l'utilisateur) et
 * filtres que le plan permet. « Tous » est toujours épinglé, en tête. */
export interface InboxFilters {
  pinned: InboxFilter[]
  available: InboxFilter[]
}

export interface InboxResponse {
  items: InboxItem[]
  total: number
  has_more: boolean
  unread_threads: number
  /** Plan Team : conversations où la personne connectée est citée (non lu). */
  mention_threads?: number
  filters?: InboxFilters
  lines: InboxLineSummary[]
}

/** Clé d'une conversation : la ligne et la personne. */
export const cleFil = (item: { phone: string; line: InboxLine | null }) => `${item.line?.number ?? ''}|${item.phone}`

/** Le nom d'une ligne : son libellé, sinon son numéro. */
export const nomLigne = (line: InboxLine, formater: (n: string) => string) => line.label || formater(line.number)

const PAGE_SIZE = 30

/** Liste des conversations, triée par dernière activité (infinite query). */
export function useInbox(filter: InboxFilter = 'all', q = '', line: string | null = null) {
  return useInfiniteQuery({
    queryKey: ['inbox', filter, q, line],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await api.get<InboxResponse>('/api/inbox/', {
        params: {
          filter,
          q: q || undefined,
          line: line || undefined,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      })
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined
      return allPages.reduce((sum, p) => sum + p.items.length, 0)
    },
  })
}

/** Non lus de toute la boîte et de chaque ligne (barre latérale). */
function useInboxSummary() {
  return useQuery({
    queryKey: ['inbox', 'count'],
    queryFn: async () => {
      const { data } = await api.get<InboxResponse>('/api/inbox/', {
        params: { filter: 'all', limit: 1, offset: 0 },
      })
      return { unread: data.unread_threads ?? 0, lines: data.lines ?? [] }
    },
  })
}

/** Nombre de conversations non lues (pastille de la barre latérale). */
export function useInboxUnreadCount() {
  return useInboxSummary().data?.unread ?? 0
}

/** Les lignes de l'entreprise avec leurs non lus. */
export function useInboxLines(): InboxLineSummary[] {
  return useInboxSummary().data?.lines ?? []
}

// Trier la boîte (un fil toutes les deux secondes) rechargeait les listes à
// chaque clic : plus de 120 requêtes/minute, des 429, et le 28 sept 2026 un
// client actif renvoyé à l'onboarding. La pastille est déjà à jour en cache ;
// on ne recharge qu'une fois le tri fini.
let rechargeBoite: ReturnType<typeof setTimeout> | undefined

function rechargerBoiteApresTri(queryClient: QueryClient) {
  clearTimeout(rechargeBoite)
  rechargeBoite = setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: ['inbox'] })
  }, 3000)
}

/** La pastille du fil disparaît tout de suite. Un chargement de la liste déjà
 * parti (ouverture depuis Contacts : liste et marquage partent ensemble)
 * rapporterait l'ancien état : on l'annule, le rechargement suit le tri. */
async function marquerLuEnCache(queryClient: QueryClient, phone: string, line: string | null) {
  await queryClient.cancelQueries({ queryKey: ['inbox'] })
  queryClient.setQueriesData<InfiniteData<InboxResponse>>(
    { queryKey: ['inbox'], predicate: (q) => q.queryKey[1] !== 'count' },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.phone === phone && (!line || item.line?.number === line) ? { ...item, unread: 0 } : item,
          ),
        })),
      },
  )
}

/** Marque un fil lu (textos, vocaux, appels manqués) sur une ligne.
 * Sans ligne (fil ouvert depuis la fiche) : toutes les lignes de la personne. */
export function useMarkThreadRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ phone, line }: { phone: string; line: string | null }) => {
      await api.post(`/api/inbox/${encodeURIComponent(phone)}/read/`, {}, {
        params: { line: line || undefined },
      })
    },
    onMutate: ({ phone, line }: { phone: string; line: string | null }) =>
      marquerLuEnCache(queryClient, phone, line),
    onSettled: () => rechargerBoiteApresTri(queryClient),
  })
}

interface FilOuvert {
  contact: Contact | null
  suggestions: ContactSuggestion[] | null
  tasks: CallTask[] | null
  timeline: TimelineResponse | null
}

/** Ouvre un fil en UNE requête : le serveur le marque lu et renvoie la fiche,
 * les propositions, les tâches et les 10 derniers échanges. Chaque morceau
 * est rangé dans le cache du bloc qui l'affiche (mêmes clés que useContact,
 * useContactSuggestions, useContactTasks, useContactTimeline) : les blocs ne
 * font aucune requête. Un morceau absent (erreur) laisse son bloc charger seul.
 * `pret` : afficher les blocs seulement une fois le cache rempli, sinon ils
 * partent chacun chercher leurs données en même temps.
 * `until` : opened on a search result, the history reaches back to that date. */
export function useOuvrirFil(phone: string | null, line: string | null, until: string | null = null) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['inbox-fil', phone, line, until],
    queryFn: async () => {
      if (!phone) return null
      await marquerLuEnCache(queryClient, phone, line)
      try {
        const { data } = await api.post<FilOuvert>(`/api/inbox/${encodeURIComponent(phone)}/open/`, {}, {
          params: { line: line || undefined, until: until || undefined },
        })
        if (data.contact) queryClient.setQueryData(['contact', phone, 5], data.contact)
        if (data.suggestions) queryClient.setQueryData(['contact-suggestions', phone], data.suggestions)
        if (data.tasks) queryClient.setQueryData(['contact-tasks', phone], data.tasks)
        if (data.timeline) {
          queryClient.setQueryData<InfiniteData<TimelineResponse, number>>(['contact-timeline', phone, line, until], {
            pages: [data.timeline],
            pageParams: [0],
          })
        }
        return data
      } finally {
        rechargerBoiteApresTri(queryClient)
      }
    },
    enabled: !!phone,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  return { pret: !phone || query.isFetchedAfterMount }
}

/** Enregistre l'ordre des lignes de l'utilisateur (barre latérale). */
export function useSaveLineOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (order: string[]) => {
      await api.put('/api/inbox/lines/order/', { order })
    },
    // Le nouvel ordre s'affiche tout de suite, sans attendre le serveur.
    onMutate: async (order: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['inbox', 'count'] })
      queryClient.setQueryData<{ unread: number; lines: InboxLineSummary[] }>(['inbox', 'count'], (data) =>
        data && {
          ...data,
          lines: [...data.lines].sort((a, b) => order.indexOf(a.number) - order.indexOf(b.number)),
        },
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })
}

/** Enregistre les filtres épinglés ; la rangée change tout de suite. */
export function useSaveInboxFilters() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pinned: InboxFilter[]) => {
      await api.put('/api/inbox/filters/', { pinned })
    },
    onMutate: async (pinned: InboxFilter[]) => {
      await queryClient.cancelQueries({ queryKey: ['inbox'] })
      queryClient.setQueriesData<InfiniteData<InboxResponse>>(
        { queryKey: ['inbox'], predicate: (q) => q.queryKey[1] !== 'count' },
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((p, i) =>
              i === 0 && p.filters ? { ...p, filters: { ...p.filters, pinned } } : p,
            ),
          },
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })
}

// ─── Collaboration d'équipe (plan Team) ──────────────────────────────────

/** Responsable et Terminé d'une conversation (en-tête du fil). */
export interface EtatFil {
  phone: string
  line: string | null
  assigned_to: MembreResume | null
  done: boolean
  done_at: string | null
  done_by: MembreResume | null
}

const cheminFil = (phone: string, action: string) => `/api/inbox/${encodeURIComponent(phone)}/${action}/`

/** L'état d'équipe d'un fil ; seulement pour un fil de ligne. */
export function useEtatFil(phone: string | null, line: string | null, enabled = true) {
  return useQuery({
    queryKey: ['inbox-state', phone, line],
    queryFn: async () => {
      const { data } = await api.get<EtatFil>(cheminFil(phone!, 'state'), { params: { line } })
      return data
    },
    enabled: !!phone && !!line && enabled,
  })
}

type ActionFil =
  | { action: 'assign'; user_id: number | null }
  | { action: 'done' }
  | { action: 'reopen' }

/** Attribuer, terminer ou rouvrir une conversation. */
export function useActionFil(phone: string, line: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (a: ActionFil) => {
      const corps = a.action === 'assign' ? { line, user_id: a.user_id } : { line }
      const { data } = await api.post<EtatFil>(cheminFil(phone, a.action), corps)
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['inbox-state', phone, line], data)
      queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })
}

/** What a team discussion hangs on: a call or a text. */
export interface SujetDiscussion {
  call?: number
  sms?: number
}

/** Publie une note interne (jamais envoyée au client). Avec `sujet` : un
 * commentaire dans la discussion de cet appel ou de ce texto. */
export function useNoteInterne(phone: string, line: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ text, mentions, sujet }: { text: string; mentions: number[]; sujet?: SujetDiscussion }) => {
      await api.post(cheminFil(phone, 'notes'), {
        line,
        text,
        mentions,
        ...(sujet?.call ? { call_id: sujet.call } : {}),
        ...(sujet?.sms ? { sms_id: sujet.sms } : {}),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-timeline', phone] })
      queryClient.invalidateQueries({ queryKey: ['discussion', phone] })
    },
  })
}

/** The team's comments on a call or a text, oldest first. */
export function useDiscussion(phone: string, sujet: SujetDiscussion | null) {
  return useQuery({
    queryKey: ['discussion', phone, sujet?.call ?? null, sujet?.sms ?? null],
    queryFn: async () => {
      const { data } = await api.get<{ notes: TimelineEvent[] }>(cheminFil(phone, 'discussion'), {
        params: sujet ?? undefined,
      })
      return data.notes
    },
    enabled: !!sujet && !!(sujet.call || sujet.sms),
  })
}

/** « X écrit… » : prévient les collègues qui ont ce fil ouvert. Sans suite
 * si le serveur refuse (plan, réseau) : ce n'est qu'un signal. */
export function signalerFrappe(phone: string, line: string | null, mode: 'sms' | 'note') {
  void api.post(cheminFil(phone, 'typing'), { line, mode }).catch(() => undefined)
}
